import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FiEye, FiHeart, FiMessageCircle, FiShare2, FiFilm } from 'react-icons/fi';

import PageHeader from '../Components/PageHeader/PageHeader';
import Breadcrumb from '../Components/Breadcrumb/Breadcrumb';
import EmptyState from '../Components/EmptyState/EmptyState';
import PlatformBadge, { getPlatform } from '../Components/PlatformBadge/PlatformBadge';
import StatBlock from '../Components/StatBlock/StatBlock';
import RefreshButton from '../Components/RefreshButton/RefreshButton';
import UploadCard from '../Components/UploadCard/UploadCard';

import {
  getLogin,
  getAccount,
  getAccountGroup,
  listUploads,
} from '../Utilities/api';

import './loginDetail.css';

function LoginDetail() {
  const { loginId } = useParams();
  const [login, setLogin] = useState(null);
  const [account, setAccount] = useState(null);
  const [group, setGroup] = useState(null);
  const [uploads, setUploads] = useState([]);

  const load = useCallback(async () => {
    const [l, ups] = await Promise.all([getLogin(loginId), listUploads(loginId)]);
    setLogin(l);
    setUploads(ups);
    if (l) {
      const a = await getAccount(l.accountId);
      setAccount(a);
      if (a) {
        const g = await getAccountGroup(a.accountGroupId);
        setGroup(g);
      }
    }
  }, [loginId]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    return uploads.reduce(
      (acc, u) => ({
        views: acc.views + (u.stats?.views || 0),
        likes: acc.likes + (u.stats?.likes || 0),
        comments: acc.comments + (u.stats?.comments || 0),
        shares: acc.shares + (u.stats?.shares || 0),
      }),
      { views: 0, likes: 0, comments: 0, shares: 0 }
    );
  }, [uploads]);

  if (!login) return null;

  const platform = getPlatform(login.platform);

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: group?.name || '…', to: group ? `/groups/${group.id}` : undefined },
          { label: account?.name || '…', to: account ? `/accounts/${account.id}` : undefined },
          { label: platform?.label || login.platform },
        ]}
      />

      <PageHeader
        title={
          <span className="login-detail-title-inner">
            <PlatformBadge platform={login.platform} size="lg" />
            <span>
              {platform?.label} — {login.platformUsername}
            </span>
          </span>
        }
        subtitle={`All uploads pulled for ${login.platformUsername} on ${platform?.label}.`}
        meta={
          <span>
            {uploads.length} {uploads.length === 1 ? 'upload' : 'uploads'}
          </span>
        }
        actions={
          <RefreshButton level="login" id={login.id} onRefreshed={load} label="Refresh" />
        }
      />

      <div className="login-detail-stats">
        <StatBlock label="Total views" value={totals.views} icon={<FiEye />} />
        <StatBlock label="Total likes" value={totals.likes} icon={<FiHeart />} />
        <StatBlock label="Total comments" value={totals.comments} icon={<FiMessageCircle />} />
        <StatBlock label="Total shares" value={totals.shares} icon={<FiShare2 />} />
      </div>

      <h2 className="login-detail-section-title">Uploads</h2>

      {uploads.length === 0 ? (
        <EmptyState
          icon={<FiFilm />}
          title="No uploads pulled yet"
          description="Hit Refresh to fetch every upload and its stats from the platform."
        />
      ) : (
        <div className="login-detail-uploads">
          {uploads.map((u) => (
            <UploadCard key={u.id} upload={u} platform={login.platform} />
          ))}
        </div>
      )}
    </>
  );
}

export default LoginDetail;
